import type { BehaviorEventInput } from './behaviorTracker';

/**
 * クリックと JS エラーの自動収集。
 *
 * クリックは document の capture フェーズで拾い、button / a / [role=button] /
 * [data-track] の最近傍祖先を対象にする。入力欄・エディタ・[data-track-ignore] の
 * 内側は記録しない。要素のテキストは 60 字まで、URL は同一オリジンのパスのみ。
 * 入力値は一切読まない。
 */

export const CLICK_TARGET_SELECTOR = 'button, a, [role="button"], [data-track]';
export const IGNORE_SELECTOR = 'input, textarea, select, [contenteditable="true"], [data-track-ignore]';
export const TEXT_MAX = 60;
export const NAME_TEXT_MAX = 30;
export const ERROR_MESSAGE_MAX = 200;
export const DEDUPE_WINDOW_MS = 300;

type Enqueue = (event: BehaviorEventInput) => void;

const normalizeText = (value: string | null | undefined): string =>
  (value ?? '').replace(/\s+/g, ' ').trim();

const sameOriginPath = (href: string): string | undefined => {
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return undefined;
    return url.pathname;
  } catch {
    return undefined;
  }
};

const buildSelector = (el: Element): string => {
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const classes = Array.from(el.classList).slice(0, 2).map((c) => `.${c}`).join('');
  return `${tag}${id}${classes}`;
};

/**
 * クリック対象の event_name と props を組み立てる。対象外なら null。
 */
export const describeClickTarget = (
  el: Element
): { event_name: string; props: Record<string, unknown> } | null => {
  if (el.closest(IGNORE_SELECTOR)) return null;

  const tag = el.tagName.toLowerCase();
  const track = normalizeText(el.getAttribute('data-track'));
  const id = el.id || undefined;
  const label = normalizeText(el.getAttribute('aria-label') || el.getAttribute('title'));
  const text = normalizeText((el as HTMLElement).innerText ?? el.textContent).slice(0, TEXT_MAX);
  const rawHref = tag === 'a' ? el.getAttribute('href') : null;
  const href = rawHref ? sameOriginPath(rawHref) : undefined;

  const eventName = track || id || label || text.slice(0, NAME_TEXT_MAX) || tag;

  const props: Record<string, unknown> = { tag, selector: buildSelector(el) };
  if (text) props.text = text;
  if (id) props.id = id;
  if (href) props.href = href;
  if (track) props.track = track;

  return { event_name: eventName, props };
};

const errorMessage = (reason: unknown): string => {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === 'string') return reason;
  try {
    return JSON.stringify(reason);
  } catch {
    return String(reason);
  }
};

/**
 * 自動収集を開始する。戻り値で解除。
 */
export const installAutocapture = (enqueue: Enqueue): (() => void) => {
  let lastTarget: Element | null = null;
  let lastAt = 0;

  const onClick = (event: Event): void => {
    const origin = event.target;
    if (!(origin instanceof Element)) return;

    const target = origin.closest(CLICK_TARGET_SELECTOR);
    if (!target) return;

    const now = Date.now();
    if (target === lastTarget && now - lastAt < DEDUPE_WINDOW_MS) return;
    lastTarget = target;
    lastAt = now;

    const described = describeClickTarget(target);
    if (!described) return;

    enqueue({ event_type: 'click', event_name: described.event_name, props: described.props });
  };

  const onError = (event: ErrorEvent): void => {
    enqueue({
      event_type: 'js_error',
      event_name: 'js_error',
      props: { message: String(event.message ?? 'error').slice(0, ERROR_MESSAGE_MAX) },
    });
  };

  const onRejection = (event: Event): void => {
    const reason = (event as Event & { reason?: unknown }).reason;
    enqueue({
      event_type: 'js_error',
      event_name: 'js_error',
      props: { message: errorMessage(reason).slice(0, ERROR_MESSAGE_MAX), unhandled_rejection: true },
    });
  };

  document.addEventListener('click', onClick, true);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    document.removeEventListener('click', onClick, true);
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
};
